import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Select, Tag } from 'antd';

import { FormattedMessage } from 'react-intl';
import FieldCheck from 'containers/TemplatePreviewPage/FieldCheck';
import Pficon from 'components/Pficon';

import messages from 'containers/TemplatePreviewPage/TemplateFlows/ComplexFlow/messages';
import { isEmpty, negate, trim } from 'lodash';

import { KEYBOARD } from 'utils/constants';
import QuestionHeader from '../QuestionHeader';

const Tags = (props) => {
  const { label, onClose } = props;
  const onPreventMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag
      color="#d8e9fe"
      onMouseDown={onPreventMouseDown}
      closable
      closeIcon={
        <Pficon
          className="color-blue-6 margin-left-tiny"
          type="close"
          size={11}
        />
      }
      onClose={onClose}
      style={{
        marginRight: 4,
        color: '#323945',
        padding: '6px 8px 3px',
        borderRadius: '4px',
        fontSize: '14px',
      }}
    >
      {label}
    </Tag>
  );
};

Tags.propTypes = {
  label: PropTypes.string,
  onClose: PropTypes.func,
};

const TextArray = ({
  question,
  onUpdateVariable,
  questionIndex,
  skipTopMargin,
  id,
}) => {
  const { min: minAnswers = 0, max: maxAnswers = 100 } = question;
  const { value = [], isPristine, hasError } = question;
  const [tempTagValue, setTempTagValue] = useState('');

  const underMinCount = Math.max(minAnswers - value.length, 0);
  const overMaxCount = Math.max(value.length - maxAnswers, 0);

  const missingMessage =
    underMinCount === 1
      ? messages.validation.fieldIncomplete
      : messages.validation.fieldIncompletePlural;

  const extraValuesMessage =
    overMaxCount === 1
      ? messages.validation.fieldHasExtra
      : messages.validation.fieldHasExtraPlural;

  const onChange = useCallback(
    (updatedValues) => {
      onUpdateVariable(
        updatedValues
          .map((updatedValue) => trim(updatedValue))
          .filter(negate(isEmpty)),
      );
    },
    [onUpdateVariable],
  );

  const tagsListener = useCallback(
    (e) => {
      const { target, key, which } = e;
      const { value: currentInputValue } = target;
      if (target.id !== `tags-${id}`) {
        return;
      }

      if (key === KEYBOARD.ENTER) {
        e.stopPropagation();
        e.preventDefault();
      }

      if (
        (key === KEYBOARD.BACKSPACE || which === 8 || which === 46) &&
        !currentInputValue
      ) {
        e.stopPropagation();
        e.preventDefault();
        onChange(value.slice(0, value.length - 1));
      }
    },
    [id, onChange, value],
  );

  const tagsChangeListener = useCallback(
    (e) => {
      const { target } = e;
      const { value: newValue } = target;
      if (target.id !== `tags-${id}`) {
        return;
      }

      setTempTagValue(newValue);
    },
    [id, setTempTagValue],
  );

  const tagsBlurListener = useCallback(
    (e) => {
      const { target } = e;
      if (target.id !== `tags-${id}` || !tempTagValue) {
        return;
      }

      if (!value.includes(tempTagValue)) {
        onChange([...value, tempTagValue]);
      }
    },
    [id, onChange, value, tempTagValue],
  );

  useEffect(() => {
    document.addEventListener('keydown', tagsListener);
    document.addEventListener('focusout', tagsBlurListener);
    document.addEventListener('change', tagsChangeListener);

    return () => {
      document.removeEventListener('keydown', tagsListener);
      document.removeEventListener('focusout', tagsBlurListener);
      document.removeEventListener('change', tagsChangeListener);
    };
  }, [tagsListener, tagsBlurListener]);

  return (
    <React.Fragment>
      <QuestionHeader
        title={question.body}
        note={question.note}
        skipTopMargin={skipTopMargin}
      />
      <div>
        <div className="position-relative">
          <Select
            mode="tags"
            tagRender={(props) => (
              <Tags
                {...props}
                onClose={() => {
                  onChange(value.filter((current) => current !== props.label));
                }}
              />
            )}
            className="w-full pf-select--tags js-select-tags template-text-array border-red-6-active border-red-6-focus"
            dropdownClassName="ant-select-dropdown-menu--hidden"
            placeholder={question.placeholder}
            autoFocus={questionIndex === 0}
            onSelect={(selectedValue) => {
              onChange([...value, selectedValue]);
            }}
            value={value}
            options={value.map((option) => ({
              key: option,
              value: option,
            }))}
            id={`tags-${id}`}
          />
          {value.length === 0 && (
            <span
              className="color-grey-8 position-absolute text-size-h4"
              style={{
                zIndex: 2,
                top: '50%',
                right: '10px',
                fontSize: '12px',
                transform: 'translate3d(0, -50%, 0)',
              }}
            >
              Press <Pficon type="enter-icon" /> to enter value
            </span>
          )}
          <FieldCheck checked={!hasError} />
        </div>
        {!isPristine && hasError && Boolean(underMinCount) && (
          <span className="color-red-6" key={`${question.id}-validation`}>
            <FormattedMessage
              {...missingMessage}
              values={{
                missingValuesCount: underMinCount,
              }}
            />
          </span>
        )}
        {!isPristine && hasError && Boolean(overMaxCount) && (
          <span className="color-red-6" key={`${question.id}-validation-count`}>
            <FormattedMessage
              {...extraValuesMessage}
              values={{
                extraValuesCount: overMaxCount,
              }}
            />
          </span>
        )}
      </div>
    </React.Fragment>
  );
};

TextArray.propTypes = {
  question: PropTypes.object,
  questionIndex: PropTypes.number,
  onUpdateVariable: PropTypes.func.isRequired,
};

export default TextArray;
